/**
 * Eine Zahl aus einem Textfeld lesen — streng, und ohne zu raten.
 *
 * ═══ WARUM KEIN `<input type="number">` ═══
 *
 * Es sah nach der einfachen Lösung aus und war die falsche. Bei einer Eingabe,
 * die keine Zahl ist, liefert der Browser den Wert `""` — dieselbe Auskunft wie
 * bei einem leeren Feld. In der Abrechnung wurde daraus beim Verlassen des
 * Feldes still `preis_vk: null`.
 *
 * Und `validity.badInput` fängt nicht alles: Chrome liest „1.250,50" nicht als
 * ungültig, sondern als 1,2505 — es wirft das Komma weg. Gespeichert wurde ein
 * Preis von 1,25 € statt 1.250,50 €, ohne Meldung, mit einem plausibel
 * aussehenden Betrag. Das ist der schlimmere Fehler: Ein gelöschter Preis fällt
 * auf, ein um den Faktor tausend falscher nicht.
 *
 * Deshalb ein Textfeld und diese eine Stelle, die entscheidet, was eine Zahl
 * ist. Erlaubt ist, was im Büro tatsächlich getippt wird:
 *
 *   12       12,5      12,50      1250,50
 *   1.250,50 (Tausenderpunkt, deutsch)      12.5  (Punkt mit 1–2 Stellen)
 *
 * Alles andere ist „ungültig" — auch „1,250.50" (englische Tausender) und
 * „0.125": Ein Punkt mit genau drei Stellen dahinter heißt im Deutschen
 * Tausender, im Englischen Dezimal, und wo beides möglich wäre, wird nicht
 * geraten, sondern nachgefragt. Eine Zahl, die still falsch gelesen wird, steht
 * hinterher auf einer Rechnung.
 *
 * Negative Zahlen gibt es hier nicht: Preise und Mengen dieser Oberfläche sind
 * nie negativ, und ein Minuszeichen ist fast immer ein Tippfehler.
 */

export type Zahl =
  | { art: 'leer' }
  | { art: 'zahl'; wert: number }
  | { art: 'ungueltig' };

export function zahlLesen(text: string): Zahl {
  const t = text.trim();
  if (t === '') return { art: 'leer' };

  let bereinigt: string | null = null;
  if (/^\d+$/.test(t)) bereinigt = t;
  else if (/^\d+,\d+$/.test(t)) bereinigt = t.replace(',', '.');
  // Deutsche Tausenderpunkte: 1.250 · 1.250,50 · 12.345.678,9
  else if (/^[1-9]\d{0,2}(\.\d{3})+(,\d+)?$/.test(t)) {
    bereinigt = t.replace(/\./g, '').replace(',', '.');
  }
  // Punkt als Dezimalzeichen, nur mit ein oder zwei Stellen dahinter.
  else if (/^\d+\.\d{1,2}$/.test(t)) bereinigt = t;

  if (bereinigt === null) return { art: 'ungueltig' };
  const wert = Number(bereinigt);
  return Number.isFinite(wert) ? { art: 'zahl', wert } : { art: 'ungueltig' };
}

/** Der Wert eines Zahlenfeldes für die Datenbank: Zahl, null für „leer" — oder `undefined`, wenn ungültig. */
export function zahlOderNull(text: string): number | null | undefined {
  const z = zahlLesen(text);
  if (z.art === 'leer') return null;
  return z.art === 'zahl' ? z.wert : undefined;
}

/** Eine Zahl so anzeigen, wie man sie tippt: „12,5", nicht „12.5". */
export function zahlAlsText(wert: number | null | undefined): string {
  return wert === null || wert === undefined ? '' : String(wert).replace('.', ',');
}
